/**
 * Sales-order transport boundary.
 *
 * Every sales read is branch-scoped, so all three go through the operational
 * fetcher and carry the shell's acting branch.
 *
 * The contract splits an order in two on purpose. The list and detail
 * projections are *minimized*: stable references, commercial facts, no
 * customer name, email, phone or address. Those live behind a separate
 * purpose-specific operation that audits both success and denial, answers
 * `Cache-Control: no-store`, and answers 410 once the retention snapshot has
 * been purged. This module keeps that split intact — nothing here merges the
 * two shapes into one convenient object.
 */
import type { components } from "@erp/api-client";

import { apiFetchOperational, type Page } from "@/lib/api/http";
import { fetchPage, type CursorQueryArgs, type ListFilters } from "@/features/shared/ops-api";

type Schemas = components["schemas"];

export type SalesOrderStatus = Schemas["SalesOrderStatus"];
export type SalesOrderSummary = Schemas["SalesOrderSummary"];
export type SalesOrder = Schemas["SalesOrder"];
export type SalesOrderLine = Schemas["SalesOrderLine"];
export type SalesOrderCustomerPii = Schemas["SalesOrderCustomerPii"];
export type AddressSnapshot = Schemas["AddressSnapshot"];
export type SalesMoney = Schemas["SalesMoney"];

/** The contract's two states — an order is drafted here or imported from a channel. */
export const SALES_ORDER_STATUSES: readonly SalesOrderStatus[] = ["draft", "imported"];

export interface SalesOrderFilters extends ListFilters {
  status?: SalesOrderStatus;
  channel_id?: string;
  customer_id?: string;
  external_ref?: string;
}

/** `listSalesOrders` — GET · sales.order.read. Ordered created_at,id descending;
 *  the endpoint publishes no sort parameter, so the screen offers none. */
export function listSalesOrders(
  filters: SalesOrderFilters,
  args: CursorQueryArgs = {},
): Promise<Page<SalesOrderSummary>> {
  return fetchPage<SalesOrderSummary>("/sales-orders", { ...args, filters });
}

/** `getSalesOrder` — GET · sales.order.read. Answers `{ order, lines }`. */
export function getSalesOrder(salesOrderId: string, signal?: AbortSignal): Promise<SalesOrder> {
  return apiFetchOperational<SalesOrder>(`/sales-orders/${encodeURIComponent(salesOrderId)}`, {
    signal,
  });
}

/** `getSalesOrderCustomerPii` — GET · sales.order.pii.read. Every call is
 *  audited, so it is only ever issued on an explicit operator action. */
export function getSalesOrderCustomerPii(
  salesOrderId: string,
  signal?: AbortSignal,
): Promise<SalesOrderCustomerPii> {
  return apiFetchOperational<SalesOrderCustomerPii>(
    `/sales-orders/${encodeURIComponent(salesOrderId)}/customer-pii`,
    { signal },
  );
}
